import { Paper, Text, Table, Badge, Progress, Group, Stack, Flex } from "@mantine/core";
import { IconTarget } from "@tabler/icons-react";
import { useTableSort, SortableTh } from "../../hooks/Direction/useTableSort";

/**
 * Devuelve color según el porcentaje de cumplimiento
 */
const getProgressColor = (percent) => {
    if (percent >= 100) return "green";
    if (percent >= 70) return "yellow";
    if (percent >= 40) return "orange";
    return "red";
};

const ACCESSORS = {
    auName: (o) => (o.auName || "").toLowerCase(),
    auType: (o) => (o.auType || "").toLowerCase(),
    auInitialDate: (o) => (o.auInitialDate ? new Date(o.auInitialDate).getTime() : null),
    inscriptionTarget: (o) => o.inscriptionTarget ?? null,
    equivalentAt100: (o) => o.equivalentAt100 ?? null,
    totalPayments: (o) => o.totalPayments ?? null,
    progressPercent: (o) => o.progressPercent ?? null,
};

/**
 * Seguimiento de objetivos de inscripción por unidad académica.
 * Muestra: nombre, tipo, objetivo, inscriptos equivalentes al 100%, progreso, pagos totales, revenue.
 */
export default function ObjectivesTracker({ objectives }) {
    const { sorted, thProps } = useTableSort(objectives, ACCESSORS);

    if (!objectives || objectives.length === 0) {
        return (
            <Paper shadow="sm" radius="md" p="md">
                <Group gap="xs" mb="sm">
                    <IconTarget size={20} />
                    <Text fw={700}>Seguimiento de Objetivos</Text>
                </Group>
                <Text c="dimmed" size="sm">No hay datos de objetivos disponibles.</Text>
            </Paper>
        );
    }

    // Resumen global
    const totalTarget = objectives.reduce((sum, o) => sum + (o.inscriptionTarget || 0), 0);
    const totalEquivalent = objectives.reduce((sum, o) => sum + (o.equivalentAt100 || 0), 0);
    const globalProgress = totalTarget > 0 ? Math.round((totalEquivalent / totalTarget) * 1000) / 10 : 0;

    return (
        <Paper shadow="sm" radius="md" p="md">
            <Group gap="xs" mb="xs">
                <IconTarget size={20} />
                <Text fw={700}>Seguimiento de Objetivos</Text>
            </Group>

            {/* Resumen global */}
            <Flex gap="lg" mb="md" wrap="wrap">
                <Stack gap={0}>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Objetivo global</Text>
                    <Text size="lg" fw={700}>
                        {totalEquivalent.toFixed(1)} / {totalTarget}
                        <Text span size="sm" c="dimmed" ml={4}>({globalProgress}%)</Text>
                    </Text>
                </Stack>
                <Stack gap={0}>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Capacitaciones</Text>
                    <Text size="lg" fw={700}>{objectives.length}</Text>
                </Stack>
            </Flex>

            <Progress
                value={Math.min(globalProgress, 100)}
                color={getProgressColor(globalProgress)}
                size="lg"
                radius="md"
                mb="md"
            />

            {/* Tabla detallada */}
            <div style={{ overflowX: 'auto' }}>
                <Table striped highlightOnHover withTableBorder fontSize="sm">
                    <Table.Thead>
                        <Table.Tr>
                            <SortableTh field="auName" {...thProps}>Capacitación</SortableTh>
                            <SortableTh field="auType" {...thProps}>Tipo</SortableTh>
                            <SortableTh field="auInitialDate" align="center" {...thProps}>Inicio</SortableTh>
                            <SortableTh field="inscriptionTarget" align="center" {...thProps}>Objetivo</SortableTh>
                            <SortableTh field="equivalentAt100" align="center" {...thProps}>Equiv. 100%</SortableTh>
                            <SortableTh field="totalPayments" align="center" {...thProps}>Pagos</SortableTh>
                            <SortableTh field="progressPercent" style={{ width: 160 }} {...thProps}>Progreso</SortableTh>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {sorted.map((obj) => {
                            const color = getProgressColor(obj.progressPercent);
                            return (
                                <Table.Tr key={obj._id}>
                                    <Table.Td>
                                        <Stack gap={0}>
                                            <Text size="sm" fw={500} lineClamp={1}>
                                                {obj.auName}
                                            </Text>
                                        </Stack>
                                    </Table.Td>
                                    <Table.Td>
                                        <Badge
                                            variant="light"
                                            size="sm"
                                            color={
                                                obj.auType === 'Diplomado' ? 'blue' :
                                                    obj.auType === 'Posgrado' ? 'violet' :
                                                        obj.auType === 'Curso' ? 'teal' : 'gray'
                                            }
                                        >
                                            {obj.auType || '-'}
                                        </Badge>
                                    </Table.Td>
                                    <Table.Td style={{ textAlign: 'center' }}>
                                        {obj.auInitialDate && (
                                            <Text size="xs" c="dimmed">
                                                {new Date(obj.auInitialDate).toLocaleDateString('es-ES', {
                                                    day: '2-digit',
                                                    month: '2-digit',
                                                    year: 'numeric',
                                                    timeZone: 'UTC'
                                                })}
                                            </Text>
                                        )}
                                    </Table.Td>

                                    <Table.Td style={{ textAlign: 'center' }}>
                                        <Text size="sm" fw={600}>{obj.inscriptionTarget}</Text>
                                    </Table.Td>
                                    <Table.Td style={{ textAlign: 'center' }}>
                                        <Text size="sm" fw={600}>{obj.equivalentAt100}</Text>
                                    </Table.Td>
                                    <Table.Td style={{ textAlign: 'center' }}>
                                        <Text size="sm">{obj.totalPayments}</Text>
                                    </Table.Td>
                                    <Table.Td>
                                        <Group gap={4} wrap="nowrap">
                                            <Progress
                                                value={Math.min(obj.progressPercent, 100)}
                                                color={color}
                                                size="md"
                                                radius="md"
                                                style={{ flex: 1 }}
                                            />
                                            <Badge
                                                color={color}
                                                variant="light"
                                                size="sm"
                                                w={55}
                                                styles={{ root: { textAlign: 'center' } }}
                                            >
                                                {obj.progressPercent}%
                                            </Badge>
                                        </Group>
                                    </Table.Td>
                                </Table.Tr>
                            );
                        })}
                    </Table.Tbody>
                </Table>
            </div>
        </Paper>
    );
}